import moment from 'moment';

import { IDatatableFieldsResult } from '../../../actions/datatableActions';
import { IItem } from '../../../entities/IFilters';
import { EFacetsType, EFilterType, IReferencial } from '../../../entities/IGlobal';
import { IPrincipalColumnInput } from '../../../entities/IPrincipalColumn';
import {
    datatablePartyRowToPickerShape,
    IPartiesDatatableElement,
    principalColumnInputToDisplay
} from '../../../tools/principalDisplayMappers';
import { IColumnSettings } from '../../../entities/IClusters';
import { IDatatableFieldsTypes } from './DatatableContext';
import { areFilterKeysAliases } from './filterColumnKeys';
import { resolveColumnLabel, resolveDatagridFieldLabel } from './resolveDatagridFieldLabel';

type DatatableFieldProperty = IDatatableFieldsResult['properties'][number];

/** Field name in filter keys (e.g. "authorities") may differ in casing from metadata ("Authorities"). */
const findFieldDefByName = (allFields: IDatatableFieldsResult[], rawFieldName: string): IDatatableFieldsResult | undefined => {
    if (!rawFieldName) {
        return undefined;
    }
    return (
        allFields.find((f) => f.field === rawFieldName) ??
        allFields.find((f) => f.field?.toLowerCase() === rawFieldName.toLowerCase())
    );
};

/**
 * Map UI filter segment (often "Id") to the property row returned in /Fields (e.g. ChildId, PrincipalId).
 * filterPath lives on these objects — if we pick the wrong property, the payload loses filterPath.
 */
const resolvePropertyDefForFilterKey = (
    fieldDef: IDatatableFieldsResult,
    propertyNameFromKey: string
): DatatableFieldProperty | undefined => {
    const props = fieldDef.properties || [];
    if (props.length === 0) {
        return undefined;
    }

    const exact = props.find((p) => p.propertyName === propertyNameFromKey);
    if (exact) {
        return exact;
    }

    const caseInsensitive = props.find(
        (p) => p.propertyName?.toLowerCase() === propertyNameFromKey?.toLowerCase()
    );
    if (caseInsensitive) {
        return caseInsensitive;
    }

    if (propertyNameFromKey?.toLowerCase() === 'id') {
        const isIdShapeProperty = (name: string | undefined) =>
            !!name && /^(?:child|principal|user)?id$/i.test(name);

        const pick = props.find(
            (p) =>
                p.isFilterable &&
                (p.propertyName === 'ChildId' ||
                    p.propertyName === 'PrincipalId' ||
                    p.propertyName === 'UserId' ||
                    isIdShapeProperty(p.propertyName))
        );
        if (pick) {
            return pick;
        }
        const idLikeAny = props.find(
            (p) =>
                p.propertyName === 'ChildId' ||
                p.propertyName === 'PrincipalId' ||
                p.propertyName === 'UserId' ||
                isIdShapeProperty(p.propertyName)
        );
        if (idLikeAny) {
            return idLikeAny;
        }
    }

    const withPath = props.filter((p) => p.filterPath);
    if (withPath.length === 1) {
        return withPath[0];
    }

    return undefined;
};

const resolveFieldAndPropertyFromCompoundFilterKey = (
    allFields: IDatatableFieldsResult[],
    compoundKey: string = ''
): { fieldName: string; fieldDef: IDatatableFieldsResult | undefined; propDef: DatatableFieldProperty | undefined } => {
    const dot = compoundKey.indexOf('.');
    if (dot <= 0) {
        return { fieldName: compoundKey, fieldDef: undefined, propDef: undefined };
    }
    const rawFieldName = compoundKey.slice(0, dot);
    const propertyNameFromKey = compoundKey.slice(dot + 1);
    const fieldDef = findFieldDefByName(allFields, rawFieldName);
    if (!fieldDef) {
        return { fieldName: rawFieldName, fieldDef: undefined, propDef: undefined };
    }
    const propDef = resolvePropertyDefForFilterKey(fieldDef, propertyNameFromKey);
    return {
        fieldName: fieldDef.field,
        fieldDef,
        propDef
    };
};

/** Aligns field metadata order with datatable column order (filters, sort UI, filter keys). */
export const sortFieldsByColumnOrder = (
    fields: IDatatableFieldsResult[],
    columnsSettings?: IColumnSettings[]
): IDatatableFieldsResult[] => {
    if (!fields?.length || !columnsSettings?.length) {
        return fields;
    }
    const indexByField = new Map<string, number>();
    columnsSettings.forEach((col, i) => {
        if (col.field) {
            indexByField.set(col.field, i);
        }
    });
    return fields
        .map((f, origIdx) => ({ f, origIdx }))
        .sort((a, b) => {
            const ia = indexByField.has(a.f.field) ? indexByField.get(a.f.field)! : Number.MAX_SAFE_INTEGER;
            const ib = indexByField.has(b.f.field) ? indexByField.get(b.f.field)! : Number.MAX_SAFE_INTEGER;
            if (ia !== ib) {
                return ia - ib;
            }
            return a.origIdx - b.origIdx;
        })
        .map(({ f }) => f);
};

export { resolveColumnLabel, resolveDatagridFieldLabel };

export enum EDatatableIds {
    userKeyDatesDatatable = 'userKeyDatesDatatable',
    userApprovalDataTable = 'userApprovalDataTable',
    approvalDatatable = 'approvalDatatable',
    approvalInsightsDatatable = 'approvalInsightsDatatable',
    exportControlInsightsDatatable = 'exportControlInsightsDatatable',
    campaignsDatatable = 'campaignsDatatable',
    campaignRecipientsDatatable = 'campaignRecipientsDatatable',
    keyDatesDatatable = 'keyDatesDatatable',
    disputeDatatable = 'disputeDatatable',
    disputeInstanceDatatable = 'disputeInstanceDatatable',
    exploreDataTable = 'exploreDataTable',
    approvalDetailsDataTable = 'approvalDetailsDataTable',
    clausierDataTable = 'clausierDataTable',
    clausierPOAClusterDatagrid = 'clausierPOAClusterDatagrid',
    clausierPOADataTable = 'clausierPOADataTable',
    faqDataTable = 'faqDataTable',
    qAndADataTable = 'qAndADataTable',
    workspaceQAndADataTable = 'workspaceQAndADataTable',
    allRightsDataTable = 'allRightsDataTable',
    mainSLDataTable = 'mainSLDataTable',
    sideLetterDataTable = 'sideLetterDataTable',
    investorsDataTable = 'investorsDataTable',
    groupMembersDataTable = 'groupMembersDataTable',
    accessRequestDataTable = 'accessRequestDataTable',
    clusterMembersDataTable = 'clusterMembersDataTable',
    pendingInvitationsDataTable = 'pendingInvitationsDataTable',
    approvalOverviewDataTable = 'approvalOverviewDataTable',
    approvalWorkflowSnapshotDataTable = 'approvalWorkflowSnapshotDataTable',
    attachmentDataTable = 'attachmentDataTable',
    landlordOwnerDataTable = 'landlordOwnerDataTable',
    categoriesDataTable = 'categoriesDataTable',
    limitsDataTable = 'limitsDataTable',
    organizationMembersDataTable = 'organizationMembersDataTable',
    pendingMembersDataTable = 'pendingMembersDataTable',
    periodsDataTable = 'periodsDataTable',
    profileClusterLinkDataTable = 'profileClusterLinkDataTable',
    profileMandateDataTable = 'profileMandateDataTable',
    delegatedActionsDataTable = 'delegatedActionsDataTable',
    groupsDataTable = 'groupsDataTable',
    singleGroupClusterLinkDataTable = 'singleGroupClusterLinkDataTable',
    singleGroupMembersDataTable = 'singleGroupMembersDataTable',
    invitationsDataTable = 'invitationsDataTable',
    membersDataTable = 'membersDataTable',
    platformUsersDataTable = 'platformUsersDataTable',
    workspacesDatatable = 'workspacesDatatable',
    legalEntityClustersDataTable = 'legalEntityClustersDataTable',
    legalEntityKeyDateClustersDataTable = 'legalEntityKeyDateClustersDataTable',
    legalEntityMandateDataTable = 'legalEntityMandateDataTable',
    workspaceApprovalDatatable = 'workspaceApprovalDatatable',
    beneficiaryTable = 'beneficiaryTable',
    donorTable = 'donorTable',
    declarationTable = 'declarationTable',
    entityKeyDatesDatatable = 'entityKeyDatesDatatable',
    sponsoringKeyDateDatatable = 'sponsoringKeyDateDatatable',
    poaKeyDateTable = 'poaKeyDateTable',
    poaDelegationChainTable = 'poaDelegationChainTable',
    legalSupportKeyDateClustersDataTable = 'legalSupportKeyDateClustersDataTable',
    defaultKeyDateDatatable = 'defaultKeyDateDatatable',
    inquiryProductInformationTable = 'inquiryProductInformationTable',
    inquiryWizardTable = 'inquiryWizardTable',
    inquiriesDetailsDataTable = 'inquiriesDetailsDataTable',
    inquiriesDataTable = 'inquiriesDataTable',
    tprmEntitiesDataTable = 'tprmEntitiesDataTable',
    tprmReviewsDataTable = 'tprmReviewsDataTable',
    testDatatable = 'testDatatable',
    inquiryResellersTable = 'inquiryResellersTable',
    clusterActionsTable = 'clusterActionsTable',
    clusterMandateDataTable = 'clusterMandateDataTable',
    linkedClustersDataTable = 'linkedClustersDataTable',
    documentsStakeDatatable = 'documentsStakeDatatable',
    ownershipStakeDatatable = 'ownershipStakeDatatable',
    workspaceActionsTable = 'workspaceActionsTable',
    clusterTemplatesDataTable = 'clusterTemplatesDataTable',
    signatureDatatable = 'signatureDatatable',
    workspaceInvitationDataTable = 'workspaceInvitationDataTable',
    invitationRulesDataTable = 'invitationRulesDataTable',
    lobbyingActionsOrganizationDataTable = 'lobbyingActionsOrganizationDataTable',
    lobbyingProjectOrganizationDataTable = 'lobbyingProjectOrganizationDataTable',
    screeningTprmParametersDataTable = 'screeningTprmParametersDataTable',
    screeningTprmResultsDataTable = 'screeningTprmResultsDataTable',
    screeningTprmDetailsDataTable = 'screeningTprmDetailsDataTable',
    screeningPersonalTprmParametersDataTable = 'screeningPersonalTprmParametersDataTable',
    screeningPersonalTprmResultsDataTable = 'screeningPersonalTprmResultsDataTable',
    screeningPersonalTprmDetailsDataTable = 'screeningPersonalTprmDetailsDataTable',
    signatureStatusDataTable = 'signatureStatusDataTable',
    clusterStatusEntityDataTable = 'clusterStatusEntityDataTable',
    menuLinksDataTable = 'menuLinksDataTable',
    labelsDataTable = 'labelsDataTable',
    usefulLinksDataTable = 'usefulLinksDataTable',
    referentialCategoriesDataTable = 'referentialCategoriesDataTable',
    referentialItemsDataTable = 'referentialItemsDataTable',
    translationGroupsDataTable = 'translationGroupsDataTable',
    translationLabelsDataTable = 'translationLabelsDataTable',
    activitiesClusterDataTable = 'activitiesClusterDataTable',
    clusterLinksDataTable = 'clusterLinksDataTable',
    poaHoldersDataTable = 'poaHoldersDataTable',
    mandateHoldersDataTable = 'mandateHoldersDataTable',
    coiDataTable = 'coiDataTable',
    participationDataTable = 'participationDataTable',
    platformLegalEntitiesDataTable = 'platformLegalEntitiesDataTable',
    sponsoringDataTable = 'sponsoringDataTable',
    tradeAssociationDataTable = 'tradeAssociationDataTable',
    eApprovalFreeDeclarationDataTable = 'eApprovalFreeDeclarationDataTable',
    eApprovalEventExpensesDataTable = 'eApprovalEventExpensesDataTable',
    eApprovalGroupingDataTable = 'eApprovalGroupingDataTable',
    eApprovalSponsoringDataTable = 'eApprovalSponsoringDataTable',
    eApprovalAllDeclarationsDataTable = 'eApprovalAllDeclarationsDataTable',
    profilePermissionsDataTable = 'profilePermissionsDataTable',
    entityMonitoringAlertsDataTable = 'entityMonitoringAlertsDataTable'
}

export enum EFieldsType {
    MultipleStringValues = 'MultipleStringValues',
    DateFromAndTo = 'DateFromAndTo',
    Amount = 'Amount',
    Boolean = 'Boolean'
}

export const prepareReferentialsData = (data: IReferencial[]) => data?.map(elem => ({ value: elem?.name }));

export const prepareReferentialData = (data: IReferencial) => ({ value: data?.name });

export const preparePillData = (data: { id: string, name: string }[]) => data?.map(elem => ({ displayName: elem?.name, id: elem?.id }));

export const prepareUserData = (data: IPrincipalColumnInput[] | null | undefined) =>
    data?.map(user => {
        const d = principalColumnInputToDisplay(user);
        return {
            id: d.childId,
            name: d.firstLine,
            picture: d.picture,
            type: d.principalType
        };
    });

export const prepareLegalEntityData = (data: { id: string, name: string, picture: string }[]) => data?.filter(le => le != undefined).map(le => ({
    id: le.id,
    name: le.name,
    picture: le.picture
}));

export const preparePartiesData = (data: IPartiesDatatableElement[] | null | undefined) =>
    data?.map(row => datatablePartyRowToPickerShape(row));

export const preparePartyData = (data: IPartiesDatatableElement) => datatablePartyRowToPickerShape(data);

export const prepareCurrencyData = (data: { number: number, unit: { uniqueKey: string } }, allUnits: IReferencial[]) => {
    return data ? {
        number: data.number,
        unit: findReferentialNameByUniqueKey(data.unit.uniqueKey, allUnits)
    } : '';
};

export const findReferentialNameByUniqueKey = (uniqueKey: string, referentials: IReferencial[]) => {
    return referentials.find(referential => referential.Key === uniqueKey)?.name;
};

export const convertNumberUnitToText = (data: { number: number, unit: { uniqueKey: string } }, referentials: IReferencial[]) => {
    return (data && data.number !== null && data.unit !== null && data.unit.uniqueKey) ? `${data.number !== null ? data.number : ''} ${findReferentialNameByUniqueKey(data.unit.uniqueKey, referentials) || ''}` : '';
};

const createValueStructure = (elem: string, filters: any, type: string) => {
    switch (type) {
        case EFieldsType.Amount:
            return {
                // tslint:disable-next-line:no-null-keyword
                maxValue: filters[elem]?.[0]?.data?.maxValue ? Number(filters[elem]?.[0]?.data?.maxValue) : null,
                // tslint:disable-next-line:no-null-keyword
                minValue: filters[elem]?.[0]?.data?.minValue ? Number(filters[elem]?.[0]?.data?.minValue) : null,
                // tslint:disable-next-line:no-null-keyword
                value: filters[elem]?.[0]?.data?.value ? Number(filters[elem]?.[0]?.data?.value) : null,
                operator: filters[elem]?.[0]?.data?.condition
            };
        case 'decisionToMakeNow':
            return {
                value: !!filters[elem]?.[0]
            };
        case EFieldsType.Boolean:
            return {
                value: filters[elem]?.[0]
            };
        case EFieldsType.DateFromAndTo:
            return {
                from: (filters)[elem]?.[0]?.fromDate,
                to: (filters)[elem]?.[0]?.toDate

            };
        case 'rowId': {
            const raw = (filters)[elem];
            // Avoid [["id"]] when filters already hold a scalar array (e.g. static MultipleStringValues filters
            // while inferredType fell back to rowId because fieldTypes/propDef were not resolved yet).
            if (Array.isArray(raw) && raw.every((x) => typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean')) {
                return { values: raw.map(String) };
            }
            return {
                values: [raw]
            };
        }
        case 'dateRanges':
            return {
                from: (filters)[elem]?.[0]?.fromDate,
                to: (filters)[elem]?.[0]?.toDate

            };
        default: return {
            values: (filters)[elem]
        };
    }
};

/** API expects { values: string[] }; rowId fallback sometimes produced { values: [[...]] }. */
const isScalarFilterValue = (x: unknown): boolean =>
    typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean';

const normalizeMultipleStringValuesPayload = (
    fieldValueTypeForApi: string,
    value: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
    if (!value || fieldValueTypeForApi !== EFieldsType.MultipleStringValues) {
        return value;
    }
    const v = value.values;
    if (!Array.isArray(v) || v.length === 0) {
        return value;
    }
    const looksLikeNestedScalarArrays =
        v.every(
            (item) =>
                Array.isArray(item) &&
                (item as unknown[]).length > 0 &&
                (item as unknown[]).every(isScalarFilterValue)
        );
    if (!looksLikeNestedScalarArrays) {
        return value;
    }
    return {
        ...value,
        values: (v as unknown[][]).flat().map(String)
    };
};

export const createFinalFilters = (filters: any, fields: IDatatableFieldsResult[], fieldTypes: IDatatableFieldsTypes) => {
    const validFieldTypes = fieldTypes || {};
    if (!Array.isArray(fields) || fields.length === 0 || !filters) {
        return [];
    }

    const dateFilterFieldName = fields.find(field => field?.properties[0]?.filterFieldType === 'DateFromAndTo')?.field;

    const transformedFields = fields.map(field => ({
        ...field,
        properties: field.properties.filter(property => property.isFilterable)
    })).filter(field => field.properties.length > 0);

    return Object.keys(filters)
        .filter(filterKey => filterKey !== 'direction' && filterKey !== 'orderBy' && filterKey !== 'query' && filterKey !== '_reloadTimestamp')
        .map(filterKey => {
            const filterValue = filters[filterKey];
            const hasValues = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;
            if (!hasValues) {
                return undefined;
            }

            const datePropertyName = fields.find(field => field?.properties[0]?.filterFieldType === 'DateFromAndTo')?.properties.find(elem => elem.isFilterable)?.propertyName;

            const { fieldName, propDef } = resolveFieldAndPropertyFromCompoundFilterKey(fields, filterKey);
            let mapped;
            if (filterKey === 'dateRanges') {
                mapped = validFieldTypes[`${dateFilterFieldName}.${datePropertyName}`];
            } else {
                mapped =
                    validFieldTypes[filterKey] ||
                    (fieldName && propDef
                        ? validFieldTypes[`${fieldName}.${propDef.propertyName}`]
                        : undefined);
            }

            const targetField = filterKey === 'dateRanges' ? dateFilterFieldName : (mapped?.item ? mapped.field : fieldName);
            const filterPath =
                filterKey === 'dateRanges'
                    ? transformedFields.find((field) => field.field === dateFilterFieldName)?.properties?.[0]?.filterPath
                    : mapped?.item?.filterPath || propDef?.filterPath;

            const fieldValueType = fields
                .find((field) => field.field === (mapped?.field === 'dateRanges' ? dateFilterFieldName : (mapped?.field || fieldName)))
                ?.properties?.find((p) => p.propertyName === (mapped?.item?.propertyName || propDef?.propertyName))
                ?.filterFieldType;

            const inferredType =
                (filters[(mapped?.field || fieldName)]?.[0]?.type) ||
                mapped?.type ||
                propDef?.filterFieldType ||
                'rowId';
            const resolvedFieldValueType = fieldValueType || 'MultipleStringValues';
            return {
                field: targetField,
                filterPath,
                fieldValueType: resolvedFieldValueType,
                value: normalizeMultipleStringValuesPayload(
                    resolvedFieldValueType,
                    createValueStructure(filterKey, filters, inferredType) as Record<string, unknown>
                )
            };
        })
        .filter(element => element !== undefined);
};

const returnIdByFacetType = (facet: any) => {
    if (facet.facetType === EFacetType.BooleanColumnFacetDto) {
        return facet.value;
    }
    if (facet.facetType === EFacetType.ReferentialFacetDto) {
        return facet.uniqueKey;
    }
    if (facet.facetType === EFacetType.CurrencyFacetDto) {
        return facet.uniqueKey;
    }
    if (facet.facetType === EFacetType.PartyColumnFacetDto) {
        return facet.id;
    }
    if (facet.facetType === EFacetType.UserFacetDto) {
        return facet.childId || facet.id;
    }
    if (facet.facetType === EFacetType.RoleColumnFacetDto) {
        return facet.roleNameTranslationKey || facet.roleName || facet.uniqueKey || facet.id || null;
    }
    if (facet.facetType === EFacetType.ResourceOverviewColumnFacetDto) {
        return facet.resourceChildId || facet.uniqueKey || facet.id || null;
    }
    return facet.uniqueKey
        || facet.id
        || facet.key
        || facet.value
        || facet.textFormatted
        || facet.name
        || facet.text
        || facet.resourceChildId
        || facet.roleNameTranslationKey
        || facet.roleName
        || null;
};

const resolveFacetLabel = (
    elem: { translationKey?: string; name?: string; roleName?: string; resourceName?: string; textFormatted?: string; value?: string; text?: string },
    intlFormatMessage
): string => {
    if (elem.translationKey) {
        return intlFormatMessage({ id: elem.translationKey });
    }
    return elem.name || elem.roleName || elem.resourceName || elem.textFormatted || elem.text || (elem.value != null ? String(elem.value) : '') || '';
};

const mappedTypeStructure = (id: string, type: string) => {
    switch (type) {
        case 'Boolean':
            return [{
                facetType: 'boolean',
                type: 'boolean',
                translationKey: 'global.yes',
                name: 'True',
                id: 'true'
            }, {
                facetType: 'boolean',
                type: 'boolean',
                translationKey: 'global.no',
                name: 'False',
                id: 'false'
            }];
        case 'DateFromAndTo':
            return [{
                facetType: 'date',
                type: 'date'
            }];
        case 'Amount':
            return [{
                facetType: 'number',
                type: 'number'
            }];
        default: return [];
    }
};

const isCustomFilterType = (type?: string): boolean =>
    type === EFieldsType.Amount || type === EFieldsType.DateFromAndTo || type === EFieldsType.Boolean;

const lowerFirst = (value: string): string => {
    if (!value) {
        return value;
    }
    return value.charAt(0).toLowerCase() + value.slice(1);
};

const toScalarString = (value: unknown): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }

    if (typeof value === 'symbol') {
        return value.toString();
    }

    return undefined;
};

const extractRawValueFromItem = (item: any, fieldName: string, propertyName: string): unknown => {
    if (!item || !fieldName) {
        return undefined;
    }

    const directValue = item[fieldName];
    if (directValue === undefined) {
        return undefined;
    }

    if (directValue === null) {
        return null;
    }

    if (typeof directValue !== 'object') {
        return directValue;
    }

    const candidateKeys = [propertyName, lowerFirst(propertyName), 'value', 'key', 'name', 'text', 'id', 'enumElement'];
    const matchedKey = candidateKeys.find((key) => key && Object.prototype.hasOwnProperty.call(directValue, key));

    if (matchedKey) {
        return directValue[matchedKey];
    }

    return directValue;
};

const toFilterItemFromRaw = (rawValue: unknown): IItem | undefined => {
    if (rawValue === undefined) {
        return undefined;
    }

    if (rawValue === null) {
        return {
            id: null as unknown as string,
            text: '(empty)',
            visible: true,
            translationKey: '',
            data: { id: null, name: '(empty)' }
        };
    }

    if (typeof rawValue !== 'object') {
        const primitiveValue = toScalarString(rawValue);

        if (primitiveValue === undefined) {
            return undefined;
        }

        return {
            id: primitiveValue,
            text: primitiveValue,
            visible: true,
            translationKey: '',
            data: { id: rawValue, name: primitiveValue }
        };
    }

    const valueAsRecord = rawValue as Record<string, any>;
    const extractedId = valueAsRecord.id ?? valueAsRecord.key ?? valueAsRecord.value ?? valueAsRecord.uniqueKey ?? valueAsRecord.enumElement;
    const extractedText = valueAsRecord.text ?? valueAsRecord.name ?? valueAsRecord.translatedValue ?? valueAsRecord.value ?? valueAsRecord.enumElement;
    const fallbackText = extractedText ?? extractedId;
    const normalizedText = toScalarString(fallbackText);

    if (normalizedText === undefined || normalizedText.trim() === '') {
        return undefined;
    }

    const normalizedId = toScalarString(extractedId) ?? normalizedText;

    return {
        id: normalizedId,
        text: normalizedText,
        visible: true,
        translationKey: '',
        data: valueAsRecord
    };
};

const buildFilterItemsFromItems = (items: any[], fieldName: string, propertyName: string): IItem[] => {
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    const rawValues = items.flatMap((item) => {
        const extracted = extractRawValueFromItem(item, fieldName, propertyName);
        if (Array.isArray(extracted)) {
            return extracted;
        }
        return [extracted];
    });

    const mapped = rawValues
        .map(toFilterItemFromRaw)
        .filter((item): item is IItem => !!item);

    const deduped = mapped.reduce<IItem[]>((acc, item) => {
        const existing = acc.some((current) => `${current.id}` === `${item.id}` && current.text === item.text);
        if (!existing) {
            acc.push(item);
        }
        return acc;
    }, []);

    return deduped.sort((a, b) => `${a.text}`.localeCompare(`${b.text}`));
};

export const createDataForFilters = (
    facets: Record<string, any[]>,
    fields: IDatatableFieldsResult[],
    intlFormatMessage,
    items: any[] = [],
    columnsSettings?: IColumnSettings[]
) => {
    if (!fields || !Array.isArray(fields) || fields.length === 0) {
        return undefined;
    }

    const fieldsInColumnOrder = sortFieldsByColumnOrder(fields, columnsSettings);

    const filteredFields = fieldsInColumnOrder
        .map(field => ({
            ...field,
            properties: field.properties.filter(prop => (prop.isFilterable))
        }))
        // Hide linkedObjects filter on Explore datatables (Profile / LE / workspace) (#67811).
        .filter(field => field.properties.length > 0 && field.field !== 'linkedObjects') || [];
    const filnalFields = [];
    for (let i = 0; i < filteredFields.length; i++) {
        const field = filteredFields[i];
        if (field.properties.length === 1) {
            filnalFields.push({
                key: field.properties[0].facetPath || field.properties[0].filterPath,
                text: resolveDatagridFieldLabel(field.field, columnsSettings, intlFormatMessage),
                data: field
            });
        } else if (field.properties.length > 1) {
            field.properties.forEach(prop => {
                filnalFields.push({
                    key: prop.facetPath || prop.filterPath,
                    text: resolveDatagridFieldLabel(field.field, columnsSettings, intlFormatMessage, prop.propertyName),
                    data: {
                        field: field.field,
                        properties: [prop]
                    }
                });
            });
        }
    }

    const fieldsData = Object.fromEntries(filnalFields.map(item => [
        `${item.data.field}.${item.data.properties[0].propertyName}`,
        {
            type: item.data.properties[0].filterFieldType,
            item: item.data.properties[0],
            field: item.data.field
        }
    ]));

    const mappingFilters = Object.fromEntries(filnalFields.map(item => {
        const property = item.data.properties?.[0];
        const filterType = property?.filterFieldType;
        const hasFacetPath = !!property?.facetPath;
        const hasFilterPath = !!property?.filterPath;

        let mappedItems: IItem[] = [];

        if (isCustomFilterType(filterType)) {
            const customOptions = mappedTypeStructure(item.key, filterType);
            mappedItems = customOptions.map<IItem>(elem => ({
                id: returnIdByFacetType(elem),
                text: resolveFacetLabel(elem, intlFormatMessage),
                visible: true,
                translationKey: '',
                data: elem
            }));
        } else if (hasFacetPath) {
            const facetValue = facets?.[property.facetPath];
            const facetArray = Array.isArray(facetValue) ? facetValue : [];
            mappedItems = facetArray.map<IItem>(elem => {
                const id = returnIdByFacetType(elem);
                const text = resolveFacetLabel(elem, intlFormatMessage);
                return { id, text, visible: true, translationKey: '', data: elem };
            });
        } else if (hasFilterPath) {
            mappedItems = buildFilterItemsFromItems(items, item.data.field, property.propertyName);
        }

        return [
            `${item.data.field}.${property?.propertyName}`,
            mappedItems
        ];
    }));
    return { mappingFilters, fieldsData };
};

enum EFacetType {
    BooleanColumnFacetDto = 'BooleanColumnFacetDto',
    ReferentialFacetDto = 'ReferentialFacetDto',
    CurrencyFacetDto = 'CurrencyFacetDto',
    PartyColumnFacetDto = 'PartyColumnFacetDto',
    UserFacetDto = 'UserFacetDto',
    RoleColumnFacetDto = 'RoleColumnFacetDto',
    ResourceOverviewColumnFacetDto = 'ResourceOverviewColumnFacetDto'
}

export interface IStaticSelectedOptionInput {
    id?: string;
    name?: string;
    text?: string;
    preventRedirection?: boolean;
    translationKey?: string;
    [key: string]: any;
}
export interface IStaticSelectedDateRangeInput extends IStaticSelectedOptionInput {
    id: string;
    fromDate: string;
    toDate: string;
    isDate: true;
}

export type TStaticSelectedRawItem = string | IStaticSelectedOptionInput | IStaticSelectedDateRangeInput | IItem;

export type TStaticSelectedRaw = Record<string, TStaticSelectedRawItem[]> | undefined | null;

const isIItemValue = (item: TStaticSelectedRawItem): item is IItem => !!item && typeof item === 'object' && 'visible' in item && 'text' in item && 'id' in item && 'data' in item;

const isDateRangeItem = (item: TStaticSelectedRawItem): item is IStaticSelectedDateRangeInput => {
    return !!item && typeof item === 'object' && 'isDate' in item && (item as IStaticSelectedDateRangeInput).isDate === true;
};

export interface IStaticSelectedHydration {
    currentUser?: { id?: string; picture?: string };
    optionLists?: Record<string, IItem[]>;
}

const idsMatch = (left?: string, right?: string): boolean =>
    !!left && !!right && `${left}` === `${right}`;

const findFilterOption = (
    optionLists: Record<string, IItem[]> | undefined,
    filterKey: string,
    id: string
): IItem | undefined => {
    if (!optionLists) {
        return undefined;
    }
    const base = filterKey.split('.')[0];
    const lists = Object.entries(optionLists)
        .filter(([key]) => key === filterKey || key.split('.')[0] === base)
        .map(([, list]) => list);

    for (const list of lists) {
        const found = list?.find(item =>
            idsMatch(`${item.id}`, id)
            || idsMatch(`${item.data?.childId}`, id)
            || idsMatch(`${item.data?.id}`, id)
        );
        if (found) {
            return found;
        }
    }
    return undefined;
};

const resolveChipExtraData = (
    filterKey: string,
    id: string,
    hydration?: IStaticSelectedHydration
): Record<string, unknown> | undefined => {
    const fromList = findFilterOption(hydration?.optionLists, filterKey, id);
    if (fromList?.data && (fromList.data.picture || fromList.data.facetType || fromList.data.type)) {
        return {
            picture: fromList.data.picture,
            facetType: fromList.data.facetType || EFacetsType.UserFacetDto,
            type: fromList.data.type || EFilterType.personal,
            childId: fromList.data.childId
        };
    }
    if (hydration?.currentUser?.id && idsMatch(hydration.currentUser.id, id)) {
        return {
            picture: hydration.currentUser.picture || '',
            facetType: EFacetsType.UserFacetDto,
            type: EFilterType.personal
        };
    }
    return undefined;
};

export const getSelectedFilterItems = (
    selectedItems: Record<string, IItem[]> | undefined,
    field: string
): IItem[] => {
    if (!selectedItems || !field) {
        return [];
    }
    if (selectedItems[field]?.length) {
        return selectedItems[field];
    }
    const alias = Object.entries(selectedItems).find(([key, items]) =>
        items?.length && areFilterKeysAliases(key, field)
    );
    return alias?.[1] || [];
};

export const setSelectedFilterItems = (
    selectedItems: Record<string, IItem[]> | undefined,
    field: string,
    items: IItem[]
): Record<string, IItem[]> => {
    const next: Record<string, IItem[]> = { ...(selectedItems || {}) };
    Object.keys(next).forEach(key => {
        if (areFilterKeysAliases(key, field)) {
            delete next[key];
        }
    });
    next[field] = items;
    return next;
};

const buildItemFromString = (value: string, displayText?: string, extraData?: Record<string, unknown>): IItem => ({
    text: displayText || value,
    id: value,
    visible: true,
    data: {
        id: value,
        name: displayText || value,
        preventRedirection: false,
        ...extraData
    },
    translationKey: undefined
});
const buildItemFromDateRange = (value: IStaticSelectedDateRangeInput): IItem => ({
    text: `${value.fromDate ? moment(value.fromDate).format('DD/MM/YYYY') : ''} - ${value.toDate ? moment(value.toDate).format('DD/MM/YYYY') : ''}`,
    id: value.id,
    visible: true,
    data: value,
    translationKey: undefined
});

const buildItemFromObject = (value: IStaticSelectedOptionInput, displayText?: string): IItem => {
    const resolvedId = value.id !== undefined ? value.id : (value.key || value.name || value.text || '');
    const text = displayText || value.text || value.name || (resolvedId === null ? '(empty)' : resolvedId);

    const data = {
        ...value,
        id: resolvedId,
        name: value.name || text,
        preventRedirection: typeof value.preventRedirection === 'boolean' ? value.preventRedirection : false
    };
    return {
        text,
        id: resolvedId,
        visible: true,
        data,
        translationKey: undefined
    };
};

export const createStaticSelected = (
    staticSelected: TStaticSelectedRaw,
    labelResolver?: (filterKey: string, id: string) => string | undefined,
    hydration?: IStaticSelectedHydration
): Record<string, IItem[]> => {
    if (!staticSelected) {
        return {};
    }
    const finalData = Object.entries(staticSelected).reduce<Record<string, IItem[]>>((acc, [key, rawItems]) => {
        if (!Array.isArray(rawItems) || rawItems.length === 0) {
            return acc;
        }
        const mappedItems = rawItems
            .filter((item): item is TStaticSelectedRawItem => item !== undefined && item !== null)
            .map(rawItem => {
                if (typeof rawItem === 'string') {
                    const overrideText = labelResolver ? labelResolver(key, rawItem) : undefined;
                    return buildItemFromString(rawItem, overrideText, resolveChipExtraData(key, rawItem, hydration));
                }

                if (isIItemValue(rawItem)) {
                    const extra = resolveChipExtraData(key, `${rawItem.id}`, hydration);
                    const mergedData = extra && (!rawItem.data?.picture || !rawItem.data?.type)
                        ? { ...rawItem.data, ...extra }
                        : rawItem.data;
                    // Re-resolve label when a persisted chip only kept the raw id as text (#65661).
                    if (rawItem.text === rawItem.id || !rawItem.text) {
                        const resolved = labelResolver?.(key, `${rawItem.id}`);
                        if (resolved) {
                            return {
                                ...rawItem,
                                text: resolved,
                                data: { ...mergedData, name: resolved }
                            };
                        }
                    }
                    return extra ? { ...rawItem, data: mergedData } : rawItem;
                }

                if (isDateRangeItem(rawItem)) {
                    return buildItemFromDateRange(rawItem);
                }

                const maybeId = (rawItem?.id || rawItem?.key || rawItem?.name || rawItem?.text || '') as string;
                const overrideTextForObject = labelResolver && maybeId ? labelResolver(key, maybeId) : undefined;
                return buildItemFromObject(rawItem, overrideTextForObject);
            })
            .filter((item): item is IItem => !!item && item.id !== undefined && !!item.text);

        if (mappedItems.length > 0) {
            acc[key] = mappedItems;
        }

        return acc;
    }, {});
    return finalData;
};

/** True when applied filters contain values that should appear as chips / list selection (#67929). */
export const hasChipHydratableFilters = (filters: Record<string, unknown> | undefined | null): boolean => {
    if (!filters) {
        return false;
    }
    return Object.entries(filters).some(([key, value]) => {
        if (key === '_reloadTimestamp' || key === 'orderBy' || key === 'direction') {
            return false;
        }
        return Array.isArray(value) && value.length > 0;
    });
};

/** Keep human-readable chip labels when rebuilding staticSelected from raw filter ids (#65661). */
export const mergeStaticSelectedPreservingLabels = (
    next: Record<string, IItem[]>,
    previous?: Record<string, IItem[]> | null
): Record<string, IItem[]> => {
    if (!previous || Object.keys(previous).length === 0) {
        return next;
    }

    return Object.entries(next).reduce<Record<string, IItem[]>>((acc, [key, items]) => {
        const previousItems = previous[key] || [];
        acc[key] = items.map(item => {
            const itemId = `${item.id}`;
            const looksLikeIdOnly = !item.text || item.text === itemId;
            if (!looksLikeIdOnly) {
                return item;
            }
            const previousMatch = previousItems.find(prev => `${prev.id}` === itemId);
            if (previousMatch?.text && previousMatch.text !== `${previousMatch.id}`) {
                return {
                    ...item,
                    text: previousMatch.text,
                    data: {
                        ...item.data,
                        ...previousMatch.data,
                        name: previousMatch.text
                    }
                };
            }
            return item;
        });
        return acc;
    }, {});
};

export const normalizeEmptyFilterValue = (item: any) => {
    if (item === null || item === '__null__' || item?.id === '__null__') {
        return { id: null, text: '(empty)', name: '(empty)' };
    }
    return item;
};

export const denormalizeEmptyFilterValue = (item: any) => {
    if (item?.id === null && item?.text === '(empty)') {
        return null;
    }
    return item;
};

export const normalizeFiltersForUI = (filters: any) => {
    if (!filters) return filters;

    const normalized = { ...filters };
    Object.keys(normalized).forEach(key => {
        if (Array.isArray(normalized[key])) {
            normalized[key] = normalized[key].map(normalizeEmptyFilterValue);
        }
    });
    return normalized;
};

export const denormalizeFiltersForAPI = (filters: any) => {
    if (!filters) return filters;

    const denormalized = { ...filters };
    Object.keys(denormalized).forEach(key => {
        if (Array.isArray(denormalized[key])) {
            denormalized[key] = denormalized[key].map(denormalizeEmptyFilterValue);
        }
    });
    return denormalized;
};

/**
 * ColoredDatagrid strips `_reloadTimestamp` before calling getData, so client-cached
 * datagrids cannot detect a reload via filter equality. Compare context timestamps instead.
 * Returns true once per timestamp change so pagination can still use the cache.
 */
export const shouldInvalidateClientDatagridCache = (
    previousReloadTimestamp: number | undefined,
    currentReloadTimestamp: number | undefined
): boolean =>
    currentReloadTimestamp !== undefined && currentReloadTimestamp !== previousReloadTimestamp;

/**
 * First createData() must wait for column settings, and for field types when /Fields metadata exists.
 * Otherwise the grid fetches once, then immediately refetches when settings/types land (#68475).
 */
export const shouldDeferDatagridCreateData = ({
    loadingDataTableView,
    fetchingSettings,
    withoutFieldsTypes,
    fields,
    fieldsTypes
}: {
    loadingDataTableView: boolean;
    fetchingSettings: boolean;
    withoutFieldsTypes?: boolean;
    fields?: unknown[] | null;
    fieldsTypes?: Record<string, unknown> | null;
}): boolean => {
    if (loadingDataTableView || fetchingSettings) {
        return true;
    }
    if (withoutFieldsTypes) {
        return false;
    }
    const hasFields = Array.isArray(fields) && fields.length > 0;
    const hasFieldTypes = !!fieldsTypes && Object.keys(fieldsTypes).length > 0;
    return hasFields && !hasFieldTypes;
};